import { ManualInterventionStatus } from '@fruitful/investment-management';
import { SPROUT_AGENT } from '@fruitful/llm-service-shared';
import { useAtomValue } from 'jotai';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import React, { useCallback, useRef, useState } from 'react';
import { AlertCircleIcon, BadgeLabel, XStack } from 'smoothie';

import { PageContentContainer } from '../../../common/components/GroveContainers';
import {
    logRocketSessionsUrl,
    stripeCustomerDashboardUrl,
    syncteraPersonUrl,
} from '../../../common/utils/LinkUtils';
import { SENDBIRD_APP_ID } from '../../../config/config';
import { IS_PRODUCTION, IS_STAGING } from '../../../config/FruitfulEnvironment';
import { useGetUserInvestmentProfileAdmin } from '../../../member-profile/hooks/useGetUserInvestmentProfileAdmin';
import { AddPartnerModal } from '../../../membership/components/AddPartnerModal';
import { CancelMembershipModal } from '../../../membership/components/CancelMembershipModal';
import { ChangeSubscriptionModal } from '../../../membership/components/ChangeSubscriptionModal';
import { RemoveMemberFromMembershipModal } from '../../../membership/components/RemoveMemberFromMembershipModal';
import { adminUserAtom } from '../../entityAtoms';
import type { MemberDetails, MembershipDetails } from '../../entityTypes';
import { MembershipMessagingModalButton } from './MembershipMessaging';

interface MembershipInfoProps {
    membershipDetails: MembershipDetails | null;
    showMessagingButton: boolean;
}

const memberGoToLabel = (user: MemberDetails, index: number) =>
    user.firstName ?? (index === 0 ? 'Primary Member' : 'Partner Member');

const sproutDashboardUrl = (stytchId: string) =>
    `/ai?agentId=${encodeURIComponent(SPROUT_AGENT.id)}&resourceId=${encodeURIComponent(stytchId)}`;

export const MembershipPageHeader = ({
    membershipDetails,
    showMessagingButton,
}: MembershipInfoProps) => {
    const adminUser = useAtomValue(adminUserAtom);
    const membershipActionMenu = useRef<Menu>(null);
    const goToMenu = useRef<Menu>(null);
    const logRocketMenu = useRef<Menu>(null);

    /** PrimeReact popup Menus do not coordinate; on web (especially static export) outside-click can miss. Close siblings when opening another. */
    const hideMembershipHeaderMenusExcept = useCallback(
        (
            except: 'membership' | 'goTo' | 'logRocket',
            event: React.SyntheticEvent,
        ) => {
            if (except !== 'membership') {
                membershipActionMenu.current?.hide(event);
            }
            if (except !== 'goTo') {
                goToMenu.current?.hide(event);
            }
            if (except !== 'logRocket') {
                logRocketMenu.current?.hide(event);
            }
        },
        [],
    );

    const [isAddPartnerModalOpen, setIsAddPartnerModalOpen] = useState(false);
    const [isCancelMembershipModalOpen, setIsCancelMembershipModalOpen] =
        useState(false);
    const [isChangeSubscriptionModalOpen, setIsChangeSubscriptionModalOpen] =
        useState(false);
    const [isRemoveMemberModalOpen, setIsRemoveMemberModalOpen] =
        useState(false);

    const [primaryUser, secondaryUser] = membershipDetails?.users ?? [];
    const { data: primaryProfile } = useGetUserInvestmentProfileAdmin(
        { userId: primaryUser?.id ?? '' },
        { enabled: primaryUser?.id != null },
    );
    const { data: secondaryProfile } = useGetUserInvestmentProfileAdmin(
        { userId: secondaryUser?.id ?? '' },
        { enabled: secondaryUser?.id != null },
    );
    const requiresManualIntervention =
        primaryProfile?.manualInterventionStatus ===
            ManualInterventionStatus.REQUIRED ||
        secondaryProfile?.manualInterventionStatus ===
            ManualInterventionStatus.REQUIRED;

    if (!membershipDetails) {
        return null;
    }

    const shouldShowModerateMessagingButton =
        adminUser.authorization.guideMessaging.canModerate;
    const shouldShowCancelMembershipButton =
        adminUser.authorization.selectedMembership.cancelMembership.canUse;
    // TEMP: hide in production until change-subscription flow is fully ready
    const shouldShowChangeSubscriptionButton =
        !IS_PRODUCTION &&
        adminUser.authorization.selectedMembership.changeSubscription.canUse;
    const shouldShowRemoveFromMembershipButton =
        adminUser.authorization.selectedMembership.removeFromMembership.canUse;
    const shouldShowAddPartnerButton =
        adminUser.authorization.selectedMembership.addPartner.canUse &&
        membershipDetails.isMembershipActive &&
        membershipDetails.users.length === 1;

    const shouldShowMembershipActionMenu =
        shouldShowRemoveFromMembershipButton ||
        shouldShowCancelMembershipButton ||
        shouldShowChangeSubscriptionButton ||
        shouldShowAddPartnerButton;

    // Go to menu items
    const goToMenuItems: Array<{
        label: string;
        command: () => void;
    }> = [
        {
            label: 'Stripe',
            command: () => {
                const stripeUser = membershipDetails.users.find(
                    (user) => user.stripeCustomerId != null,
                );
                window.open(
                    stripeCustomerDashboardUrl(
                        stripeUser?.stripeCustomerId ?? '',
                    ),
                    '_blank',
                );
            },
        },
        {
            label: `Synctera - ${membershipDetails.users[0]?.firstName ?? 'Primary Member'}`,
            command: () => {
                window.open(
                    syncteraPersonUrl(
                        membershipDetails.users[0]?.syncteraPersonId ?? '',
                    ),
                    '_blank',
                );
            },
        },
        {
            label: `Copy Synctera URL - ${membershipDetails.users[0]?.firstName ?? 'Primary Member'}`,
            command: async () => {
                await navigator.clipboard.writeText(
                    syncteraPersonUrl(
                        membershipDetails.users[0]?.syncteraPersonId ?? '',
                    ),
                );
            },
        },
    ];

    // LogRocket menu items
    const logRocketMenuItems: Array<{
        label: string;
        command: () => void;
    }> = [
        {
            label: `iOS - ${membershipDetails.users[0]?.firstName ?? 'Primary Member'}`,
            command: () => {
                window.open(
                    logRocketSessionsUrl({
                        userId: membershipDetails.users[0]?.id ?? '',
                        platform: 'ios',
                    }),
                    '_blank',
                );
            },
        },
        {
            label: `Web - ${membershipDetails.users[0]?.firstName ?? 'Primary Member'}`,
            command: () => {
                window.open(
                    logRocketSessionsUrl({
                        userId: membershipDetails.users[0]?.id ?? '',
                        platform: 'web',
                    }),
                    '_blank',
                );
            },
        },
    ];

    // Partner member
    if (membershipDetails.users.length > 1) {
        goToMenuItems.push(
            {
                label: `Synctera - ${membershipDetails.users[1]?.firstName ?? 'Partner Member'}`,
                command: () => {
                    window.open(
                        syncteraPersonUrl(
                            membershipDetails.users[1]?.syncteraPersonId ?? '',
                        ),
                        '_blank',
                    );
                },
            },
            {
                label: `Copy Synctera URL - ${membershipDetails.users[1]?.firstName ?? 'Primary Member'}`,
                command: async () => {
                    await navigator.clipboard.writeText(
                        syncteraPersonUrl(
                            membershipDetails.users[1]?.syncteraPersonId ?? '',
                        ),
                    );
                },
            },
        );

        // Add LogRocket items for second member
        logRocketMenuItems.push(
            {
                label: `iOS - ${membershipDetails.users[1]?.firstName ?? 'Partner Member'}`,
                command: () => {
                    window.open(
                        logRocketSessionsUrl({
                            userId: membershipDetails.users[1]?.id ?? '',
                            platform: 'ios',
                        }),
                        '_blank',
                    );
                },
            },
            {
                label: `Web - ${membershipDetails.users[1]?.firstName ?? 'Partner Member'}`,
                command: () => {
                    window.open(
                        logRocketSessionsUrl({
                            userId: membershipDetails.users[1]?.id ?? '',
                            platform: 'web',
                        }),
                        '_blank',
                    );
                },
            },
        );
    }

    membershipDetails.users.forEach((user, index) => {
        if (user.stytchId === '') {
            return;
        }

        goToMenuItems.push({
            label: `Sprout - ${memberGoToLabel(user, index)}`,
            command: () => {
                window.open(sproutDashboardUrl(user.stytchId), '_blank');
            },
        });
    });

    // Add Sendbird only if user has moderate permission and a channel exists
    const messagingChannelUrl = membershipDetails.messagingChannel?.channelUrl;
    if (shouldShowModerateMessagingButton && messagingChannelUrl != null) {
        goToMenuItems.push({
            label: 'Sendbird',
            command: () => {
                window.open(
                    `https://dashboard.sendbird.com/${SENDBIRD_APP_ID}/group-channels/${messagingChannelUrl}`,
                    '_blank',
                );
            },
        });
    }

    const membershipActionMenuItems: Array<{
        label: string;
        command: () => void;
    }> = [];

    if (shouldShowAddPartnerButton) {
        membershipActionMenuItems.push({
            label: 'Add Partner',
            command: () => {
                setIsAddPartnerModalOpen(true);
            },
        });
    }

    if (shouldShowChangeSubscriptionButton) {
        membershipActionMenuItems.push({
            label: 'Change subscription',
            command: () => {
                setIsChangeSubscriptionModalOpen(true);
            },
        });
    }

    if (shouldShowCancelMembershipButton) {
        membershipActionMenuItems.push({
            label: 'Cancel membership',
            command: () => {
                setIsCancelMembershipModalOpen(true);
            },
        });
    }

    if (
        shouldShowRemoveFromMembershipButton &&
        membershipDetails.users.length > 1
    ) {
        membershipActionMenuItems.push({
            label: 'Remove from membership',
            command: () => {
                setIsRemoveMemberModalOpen(true);
            },
        });
    }

    return (
        <PageContentContainer
            padding={0}
            marginTop={0}
            $lg={{ padding: 0, marginHorizontal: '1vw' }}
            backgroundColor="transparent"
            flexDirection="row"
            justifyContent="space-between"
        >
            <XStack alignItems="center" gap={8}>
                {requiresManualIntervention && (
                    <BadgeLabel
                        icon={AlertCircleIcon}
                        size="regular"
                        text="Intervention Required - IM"
                        variant="warning"
                    />
                )}
            </XStack>
            <XStack justifyContent="flex-end" gap="1vw">
                {shouldShowMembershipActionMenu && (
                    <>
                        <Menu
                            ref={membershipActionMenu}
                            model={membershipActionMenuItems}
                            popup
                            popupAlignment="right"
                        />
                        <Button
                            label="Membership Actions"
                            size="small"
                            onClick={(event) => {
                                hideMembershipHeaderMenusExcept(
                                    'membership',
                                    event,
                                );
                                membershipActionMenu.current?.toggle(event);
                            }}
                        />
                    </>
                )}

                <Menu
                    ref={goToMenu}
                    model={goToMenuItems}
                    popup
                    popupAlignment="right"
                />
                <Button
                    label="Go to"
                    size="small"
                    onClick={(event) => {
                        hideMembershipHeaderMenusExcept('goTo', event);
                        goToMenu.current?.toggle(event);
                    }}
                />

                {(IS_PRODUCTION || IS_STAGING) && (
                    <>
                        <Menu
                            ref={logRocketMenu}
                            model={logRocketMenuItems}
                            popup
                            popupAlignment="right"
                        />
                        <Button
                            label="LogRocket"
                            size="small"
                            onClick={(event) => {
                                hideMembershipHeaderMenusExcept(
                                    'logRocket',
                                    event,
                                );
                                logRocketMenu.current?.toggle(event);
                            }}
                        />
                    </>
                )}

                {showMessagingButton && (
                    <MembershipMessagingModalButton
                        membership={membershipDetails}
                    />
                )}
            </XStack>
            <AddPartnerModal
                isOpen={isAddPartnerModalOpen}
                onClose={() => {
                    setIsAddPartnerModalOpen(false);
                }}
                membershipDetails={membershipDetails}
            />
            <CancelMembershipModal
                isOpen={isCancelMembershipModalOpen}
                onClose={() => {
                    setIsCancelMembershipModalOpen(false);
                }}
                membershipDetails={membershipDetails}
            />
            <ChangeSubscriptionModal
                isOpen={isChangeSubscriptionModalOpen}
                onClose={() => {
                    setIsChangeSubscriptionModalOpen(false);
                }}
                membershipDetails={membershipDetails}
            />
            <RemoveMemberFromMembershipModal
                isOpen={isRemoveMemberModalOpen}
                onClose={() => {
                    setIsRemoveMemberModalOpen(false);
                }}
                membershipDetails={membershipDetails}
            />
        </PageContentContainer>
    );
};
